import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getUserProfile, updateUserProfile } from '../services/profileService'
import { useForm } from 'react-hook-form'

export function useProfile({ axiosPrivate, token, setAuth }) {
    const queryClient = useQueryClient()
    const [disabled, setDisabled] = useState(true)
    const [editDisabled, setEditDisabled] = useState(false)
    const [message, setMessage] = useState(null)
    const QUERYKEYS = {
        GETUSERPROFILE: ['getUserProfile'],
        UPDATEUSERPROFILE: ['updateUserProfile'],
    }
    // Fetch user profile
    const userProfileData = useQuery({
        queryKey: QUERYKEYS.GETUSERPROFILE,
        queryFn: async () => await getUserProfile({ axiosPrivate, token }),
    })
    // React Hook Form setup
    const { handleSubmit, control, reset } = useForm({
        defaultValues: {
            firstName: '',
            lastName: '',
            airline_id: '',
            staffNumber: '',
            mobile: '',
            email: '',
            status: '',
        },
        values: userProfileData?.data?.user,
    })
    // Update user profile mutation
    const updateMutation = useMutation({
        mutationKey: QUERYKEYS.UPDATEUSERPROFILE,
        mutationFn: (data) => updateUserProfile({ axiosPrivate, token, data }),
        onMutate: async (data) => {
            await queryClient.cancelQueries({ queryKey: QUERYKEYS.GETUSERPROFILE })
            const previousData = queryClient.getQueryData(QUERYKEYS.GETUSERPROFILE)
            queryClient.setQueryData(QUERYKEYS.GETUSERPROFILE, (old) => ({
                ...old,
                user: { ...old.user, ...data },
            }))
            setAuth((prev) => ({
                ...prev,
                firstName: data.firstName,
                lastName: data.lastName,
            }))
            setDisabled(true)
            setEditDisabled(false)
            return { previousData }
        },
        onError: (error, newData, context) => {
            queryClient.setQueryData(QUERYKEYS.GETUSERPROFILE, context.previousData)
            setMessage({
                type: 'Profile Update Error',
                text: error.message || 'An error occurred while updating the profile.',
            })
            setAuth((prev) => ({
                ...prev,
                firstName: context.previousData.user.firstName,
                lastName: context.previousData.user.lastName,
            }))
        },
        onSuccess: (data) => {
            if (data?.user) {
                reset(data.user)
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: QUERYKEYS.GETUSERPROFILE })
        },
    })
    // Toggles edit form to save form mode
    const handleClick = useCallback(() => {
        setDisabled(false)
        setEditDisabled(true)
    }, [])
    // Handles form submission for profile updates
    function handleProfileSubmit(data) {
        if (!data.firstName.trim() || !data.lastName.trim()) {
            if (!data.firstName.trim()) {
                setMessage({
                    type: 'Entry Error',
                    text: 'First Name field requires text entry',
                })
            }
            if (!data.lastName.trim()) {
                setMessage({
                    type: 'Entry Error',
                    text: 'Last Name field requires text entry',
                })
            }
            return
        }
        setDisabled(true)
        setEditDisabled(true)
        updateMutation.mutate(data)
    }
    return {
        userProfileData,
        updateMutation,
        message,
        setMessage,
        disabled,
        editDisabled,
        handleClick,
        handleSubmit,
        control,
        reset,
        handleProfileSubmit,
    }
}
